import { Icon, Input, InputProps } from "@ttc3k/trekker";
import { Search, Xmark } from "iconoir-react";
import { forwardRef } from "react";
import { Box, BoxProps } from "styled-system/jsx";
import { cx } from "styled-system/css";
import { dateInputRecipe } from "../DateInput/dateInput.recipe";

type SearchInputProps = InputProps & {
  onClearSearch?: () => void;
  _containerProps?: BoxProps;
  /** Match DateInput compact height (38px) for inline filter bars. */
  compact?: boolean;
};

const searchIcon = (
  isSearching: boolean,
  onClearSearch?: () => void,
) => (
  <Icon
    Element={isSearching ? Xmark : Search}
    size="sm"
    color="icon.mid"
    strokeWidth={1.75}
    cursor={isSearching ? "pointer" : "default"}
    onClick={() => isSearching && onClearSearch?.()}
  />
);

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { onClearSearch, _containerProps, compact = false, ...props },
    ref,
  ) {
    const isSearching =
      typeof props.value === "string" && props.value.length > 0;

    if (compact) {
      return (
        <Box
          w="full"
          h="38px"
          minH="38px"
          display="flex"
          alignItems="center"
          gap="100"
          className={cx(dateInputRecipe())}
          _hover={{
            outlineColor: "border.mid",
            boxShadow: "0 0 0 2px var(--wa-colors-border-mid)",
          }}
          _focusWithin={{
            outlineColor: "border.dark",
            boxShadow: "0 0 0 2px var(--wa-colors-border-dark)",
          }}
          {..._containerProps}
        >
          <Input
            flex="1"
            minW="0"
            h="full"
            border="none"
            outline="none"
            boxShadow="none"
            bg="transparent"
            px="0"
            py="0"
            ref={ref}
            aria-label={props.label || props.placeholder}
            _focus={{ boxShadow: "none", outline: "none" }}
            _focusVisible={{ boxShadow: "none", outline: "none" }}
            {...props}
          />
          <Box flexShrink={0} pr="150" display="flex" alignItems="center">
            {searchIcon(isSearching, onClearSearch)}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        w="full"
        position="relative"
        display="flex"
        alignItems="center"
        {..._containerProps}
      >
        <Input
          flex="1"
          pr="400"
          ref={ref}
          aria-label={props.label || props.placeholder}
          {...props}
        />
        <Box
          position="absolute"
          top="0"
          bottom="0"
          right="150"
          display="flex"
          alignItems="center"
          pointerEvents="none"
          css={{ "& > *": { pointerEvents: "auto" } }}
        >
          {searchIcon(isSearching, onClearSearch)}
        </Box>
      </Box>
    );
  },
);
